import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Box, CircularProgress, Tabs, Tab } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Add as AddIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { listResource, getResource, createResource, updateResource, apiFetch } from '@diggens/api-client';
import { VehicleScenarioGrid, MetricCard, SaveScenarioDialog } from '../components/analytics';

interface Metrics {
  xirr: number | null;
  tvpi: number | null;
  dpi: number | null;
  rvpi: number | null;
}

interface CashflowRow {
  id: string;
  section: 'actual' | 'projected' | 'metrics';
  date: string | null;
  description: string;
  type: 'capital_call' | 'distribution' | 'nav' | 'metric';
  amount: number | null;
  source: 'Actual' | 'Scenario';
  editable: boolean;
}

interface Scenario {
  id: string;
  name: string;
  description?: string;
  projectedCashflows: CashflowRow[];
  metrics: Metrics;
  createdAt: string;
}

export const VehicleScenarioPage = () => {
  const { id } = useParams<{ id: string }>();
  const [actualMetrics, setActualMetrics] = useState<Metrics>({ xirr: null, tvpi: null, dpi: null, rvpi: null });
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [activeTab, setActiveTab] = useState(0); // 0 = Actual, 1+ = scenarios
  const [isCalculating, setIsCalculating] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [currentProjectedCashflows, setCurrentProjectedCashflows] = useState<CashflowRow[]>([]);
  const [currentProjectedMetrics, setCurrentProjectedMetrics] = useState<Metrics>({ xirr: null, tvpi: null, dpi: null, rvpi: null });

  // Fetch vehicle details
  const { data: vehicle, isLoading } = useQuery({
    queryKey: ['vehicle', id],
    queryFn: async () => {
      if (!id) throw new Error('Vehicle ID is required');
      const data = await getResource('nodes', id);
      if (!data) throw new Error('Vehicle not found');
      return data;
    },
    enabled: !!id,
  });

  // Load initial metrics and scenarios
  useEffect(() => {
    if (vehicle?.id) {
      loadMetrics();
      loadScenarios();
    }
  }, [vehicle?.id]);

  const loadMetrics = async () => {
    if (!vehicle?.id) return;

    try {
      const data = await apiFetch<any>('/fund-metrics/calculate', {
        method: 'POST',
        body: JSON.stringify({ vehicle_id: vehicle.id }),
      });

      const metrics = {
        xirr: data.metrics.xirr,
        tvpi: data.metrics.tvpi,
        dpi: data.metrics.dpi,
        rvpi: data.metrics.rvpi,
      };

      setActualMetrics(metrics);
      setCurrentProjectedMetrics(metrics);
    } catch (error) {
      console.error('Error loading metrics:', error);
    }
  };

  const loadScenarios = async () => {
    if (!vehicle?.id) return;

    try {
      const { data } = await listResource('nodes', {
        select: '*, edges!edges_src_fkey(dst)',
        type: 'Document',
        'props->>document_type': 'scenario_analysis',
        'edges.dst': vehicle.id,
      });

      const loadedScenarios: Scenario[] = (data || []).map((doc: any) => ({
        id: doc.id,
        name: doc.props.name,
        description: doc.props.description,
        projectedCashflows: doc.props.scenario_data?.projected_cashflows || [],
        metrics: doc.props.scenario_data?.metrics || { xirr: null, tvpi: null, dpi: null, rvpi: null },
        createdAt: doc.created_at,
      }));

      setScenarios(loadedScenarios);
    } catch (error) {
      console.error('Error loading scenarios:', error);
    }
  };

  const handleSaveScenario = async (name: string, description: string) => {
    if (!vehicle?.id) return;

    try {
      const docNode = await createResource('nodes', {
        type: 'Document',
        tenant_id: vehicle.tenant_id,
        props: {
          name,
          description,
          document_type: 'scenario_analysis',
          scenario_data: {
            projected_cashflows: currentProjectedCashflows,
            metrics: currentProjectedMetrics,
          },
        },
      });

      await createResource('edges', {
        type: 'HAS_DOCUMENT',
        src: docNode.id,
        dst: vehicle.id,
        tenant_id: vehicle.tenant_id,
        props: {},
      });

      const newScenario: Scenario = {
        id: docNode.id,
        name,
        description,
        projectedCashflows: currentProjectedCashflows,
        metrics: currentProjectedMetrics,
        createdAt: docNode.created_at,
      };

      setScenarios([...scenarios, newScenario]);
      setActiveTab(scenarios.length + 1);
    } catch (error) {
      console.error('Error saving scenario:', error);
    }
  };

  const handleAutoSaveScenario = async () => {
    if (!vehicle?.id || activeTab === 0) return; // Don't auto-save Actual tab

    const currentScenario = scenarios[activeTab - 1];
    if (!currentScenario) return;

    try {
      await updateResource('nodes', currentScenario.id, {
        props: {
          name: currentScenario.name,
          description: currentScenario.description,
          document_type: 'scenario_analysis',
          scenario_data: {
            projected_cashflows: currentProjectedCashflows,
            metrics: currentProjectedMetrics,
          },
        },
      });

      // Update local state
      const updatedScenarios = scenarios.map((s) => (s.id === currentScenario.id ? { ...s, projectedCashflows: currentProjectedCashflows, metrics: currentProjectedMetrics } : s));
      setScenarios(updatedScenarios);
    } catch (error) {
      console.error('Error auto-saving scenario:', error);
    }
  };

  // const handleDeleteScenario = async (scenarioId: string) => {
  //   try {
  //     await updateResource('nodes', scenarioId, { deleted_at: new Date().toISOString() });

  //     setScenarios(scenarios.filter((s) => s.id !== scenarioId));
  //     setActiveTab(0);
  //   } catch (error) {
  //     console.error('Error deleting scenario:', error);
  //   }
  // };procee

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    // Don't change tab if clicking "+ New" (last tab)
    if (newValue === scenarios.length + 1) {
      return;
    }

    setActiveTab(newValue);
    if (newValue === 0) {
      setCurrentProjectedMetrics(actualMetrics);
      setCurrentProjectedCashflows([]);
    } else if (newValue <= scenarios.length) {
      const scenario = scenarios[newValue - 1];
      if (scenario) {
        setCurrentProjectedMetrics({ ...scenario.metrics });
        // Deep clone to prevent mutation
        setCurrentProjectedCashflows(JSON.parse(JSON.stringify(scenario.projectedCashflows)));
      }
    }
  };

  const handleNewScenario = () => {
    setActiveTab(0);
    setCurrentProjectedMetrics(actualMetrics);
    setCurrentProjectedCashflows([]);
  };

  const currentMetrics = activeTab === 0 ? actualMetrics : currentProjectedMetrics;

  if (isLoading || !vehicle) {
    return (
      <Container maxWidth={false} sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 3 }}>
      {/* Scenario Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Actual" />
          {scenarios.map((scenario) => (
            <Tab key={scenario.id} label={scenario.name} />
          ))}
          <Tab icon={<AddIcon />} iconPosition="start" label="New" onClick={handleNewScenario} />
        </Tabs>
      </Box>

      {/* Metric Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard label="XIRR" actualValue={actualMetrics.xirr} projectedValue={currentMetrics.xirr} format="percentage" isLoading={isCalculating} showProjectedFirst={activeTab > 0} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard label="TVPI" actualValue={actualMetrics.tvpi} projectedValue={currentMetrics.tvpi} format="multiple" isLoading={isCalculating} showProjectedFirst={activeTab > 0} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard label="DPI" actualValue={actualMetrics.dpi} projectedValue={currentMetrics.dpi} format="multiple" isLoading={isCalculating} showProjectedFirst={activeTab > 0} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard label="RVPI" actualValue={actualMetrics.rvpi} projectedValue={currentMetrics.rvpi} format="multiple" isLoading={isCalculating} showProjectedFirst={activeTab > 0} />
        </Grid>
      </Grid>

      {/* Cashflows Grid */}
      <VehicleScenarioGrid
        vehicleId={vehicle.id}
        vehicleName={vehicle.props.name as string}
        initialProjectedCashflows={currentProjectedCashflows}
        isActualTab={activeTab === 0}
        onMetricsUpdate={(metrics: Metrics) => {
          setCurrentProjectedMetrics(metrics);
          setIsCalculating(false);
          // Auto-save if on a scenario tab
          if (activeTab > 0) {
            setTimeout(() => handleAutoSaveScenario(), 500);
          }
        }}
        onCashflowsChange={setCurrentProjectedCashflows}
        onCalculatingChange={setIsCalculating}
        onSaveScenario={() => {
          if (activeTab === 0) {
            setSaveDialogOpen(true);
          } else {
            handleAutoSaveScenario();
          }
        }}
      />

      {/* Save Scenario Dialog */}
      <SaveScenarioDialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} onSave={handleSaveScenario} />
    </Container>
  );
};
